// Contact page — make contact with the Village, routed by category.
import { useState } from "react";
import { toast } from "sonner";
import { Mail, Handshake, Users } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PageHeader, Kicker } from "@/components/Section";
import { Field, TextInput, TextArea, Select, SubmitBar, SuccessPanel, FormShell } from "@/lib/forms";
import { submitContact, errText } from "@/lib/api";
import { useOrgSettings } from "@/lib/org";

const CATEGORIES = [
  "General Inquiry", "Partnership", "School / Educational Institution",
  "Brand or Sponsor", "Mentor", "Cohort / Applicant Question", "Media / Press",
];

const EmailCard = ({ icon: Icon, label, blurb, email, testId }) => (
  <div className="border border-silver/20 bg-charcoal/70 p-6 transition-colors hover:border-gold/50">
    <span className="flex h-10 w-10 items-center justify-center border border-gold/40 text-gold"><Icon size={18} strokeWidth={1.6} /></span>
    <h3 className="mt-4 font-head text-base font-extrabold uppercase tracking-[0.08em] text-cream">{label}</h3>
    <p className="mt-1.5 font-body text-sm leading-relaxed text-silver/70">{blurb}</p>
    <a href={`mailto:${email}`} data-testid={testId}
      className="mt-3 inline-block break-all font-mono text-xs font-bold uppercase tracking-[0.1em] text-goldlight underline decoration-gold/40 underline-offset-4 transition-colors hover:text-gold">
      {email}
    </a>
  </div>
);

export default function Contact() {
  const org = useOrgSettings();
  const [v, setV] = useState({ name: "", email: "", category: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (val) => setV((p) => ({ ...p, [k]: val }));

  const submit = async (e) => {
    e.preventDefault();
    if (!v.category) return toast.error("Choose a reason for contact.");
    setLoading(true);
    try {
      const res = await submitContact(v);
      setDone(res);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        kicker="Open Channel"
        titleLines={["Make", "contact."]}
        subtitle="Whether you're a student, a school, a brand, or a believer — there's a direct line into the Village."
        dark
      />

      <section className="grain-dark border-b border-gold/20 bg-onyx py-16 sm:py-20">
        <div className="mx-auto max-w-[1100px] px-4 sm:px-6">
          <Reveal><Kicker>Direct lines</Kicker></Reveal>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            <EmailCard icon={Mail} label="General" blurb="Questions, ideas, media, and everything else." email={org.email_general} testId="contact-email-general" />
            <EmailCard icon={Handshake} label="Partnerships" blurb="Schools, sponsors, brands, foundations, and mentors." email={org.email_partnerships} testId="contact-email-partnerships" />
            <EmailCard icon={Users} label="Cohort" blurb="Applicants and cohort member communication." email={org.email_cohort} testId="contact-email-cohort" />
          </div>
        </div>
      </section>

      <section className="grain-dark relative border-b border-gold/20 bg-charcoal py-16 sm:py-20">
        <div className="halftone-gold pointer-events-none absolute inset-0 opacity-[0.03]" />
        <div className="relative mx-auto max-w-[860px] px-4 text-center sm:px-6" data-testid="partner-with-village">
          <Reveal>
            <Kicker>Partner With The Village</Kicker>
            <h2 className="mt-5 font-display text-3xl uppercase leading-[0.95] tracking-tight text-metallic sm:text-5xl">
              Real opportunities need <span className="text-gold-metallic">real partners.</span>
            </h2>
            <p className="mx-auto mt-5 max-w-xl font-body text-base leading-relaxed text-silver/85">
              Big Little Village works with schools, mentors, brands, businesses, foundations, and community organizations to create
              meaningful real-world opportunities for emerging creatives. Our founding cohorts will launch in {org.founding_location}.
            </p>
            <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.25em] text-silver/60">For partnership opportunities:</p>
            <a href={`mailto:${org.email_partnerships}`} data-testid="partner-email-link"
              className="mt-2 inline-block font-mono text-sm font-bold uppercase tracking-[0.1em] text-goldlight underline decoration-gold/40 underline-offset-4 transition-colors hover:text-gold">
              {org.email_partnerships}
            </a>
          </Reveal>
        </div>
      </section>

      <section className="grain-dark bg-onyx py-16 sm:py-24">
        <div className="mx-auto max-w-[860px] px-4 sm:px-6">
          {done ? (
            <SuccessPanel
              title="Transmission received."
              id={done.id}
              note="Your message is in the Village inbox. We route every message to the right person and respond as quickly as we can."
            />
          ) : (
            <FormShell marker="say hello ✦">
              <form onSubmit={submit} data-testid="contact-form">
                <h2 className="font-display text-3xl uppercase tracking-tight text-metallic sm:text-4xl">Send a message</h2>
                <p className="mt-2 font-body text-sm text-silver/70">Pick the reason that fits best — it helps us route your message to the right desk.</p>
                <div className="mt-8 grid gap-5 sm:grid-cols-2">
                  <Field label="Your name" required testId="field-contact-name">
                    <TextInput value={v.name} onChange={set("name")} required testId="contact-name-input" placeholder="Full name" />
                  </Field>
                  <Field label="Email" required testId="field-contact-email">
                    <TextInput type="email" value={v.email} onChange={set("email")} required testId="contact-email-input" placeholder="you@email.com" />
                  </Field>
                </div>
                <div className="mt-5">
                  <Field label="Reason for contact" required testId="field-contact-category">
                    <Select value={v.category} onChange={set("category")} options={CATEGORIES} required testId="contact-category-select" />
                  </Field>
                </div>
                <div className="mt-5">
                  <Field label="Your message" required testId="field-contact-message">
                    <TextArea value={v.message} onChange={set("message")} required rows={6} testId="contact-message-input" placeholder="Tell us what's on your mind..." />
                  </Field>
                </div>
                <SubmitBar loading={loading} label="Send Message" loadingLabel="Transmitting..." testId="contact-submit-btn" />
              </form>
            </FormShell>
          )}
        </div>
      </section>
    </div>
  );
}
